import { styled, css, useTheme } from '@vebox/etui-system';
import '../utilities/format-ms.js';
import { createTransition } from '../utilities/create-transition.js';
import { createElement } from 'react';
import { getStyleProp } from '../utilities/get-styleprop.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div, A } from '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import Icon from '../Icon/Icon.js';
import '@vebox/etui-icons';
import '../Icon/icons.js';
import '../Icon/helper.js';
import { darken, fade } from '@vebox/etui-colors';
import 'react-spring/renderprops.cjs';
import Badge from '../Badge/Badge.js';

const _excluded = ["isToggle", "isBottom", "isDisabled", "theme"];
const HeaderNavItemDiv = styled(Div).attrs(_ref => {
  let {
      isToggle,
      isBottom,
      isDisabled,
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    flexShrink: 0,
    outline: 0,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? theme.palette.action.disabledOpacity : 1,
    minHeight: isToggle || isBottom ? theme.shape.headerBottomHeight : [theme.shape.headerBottomHeight, theme.shape.headerTopHeight],
    transition: createTransition(['color', 'background-color'], {
      duration: 'shorter'
    })
  }, props);
})`
  ${({
  theme,
  isToggle,
  isBottom,
  isActive,
  isToggleActive,
  isDisabled
}) => {
  if (isToggle) {
    return css`
        color: ${isToggleActive ? theme.palette.common.white : theme.palette.text.primary};
        border-right: 1px solid
          ${isToggleActive ? darken(theme.palette.black[80], 0.1) : theme.palette.divider};
        background-color: ${isToggleActive ? theme.palette.black[80] : theme.palette.common.white};

        &:hover,
        &:active {
          background-color: ${isDisabled ? 'transparent' : isToggleActive ? darken(theme.palette.black[80], theme.palette.tonalOffset) : fade(theme.palette.text.secondary, 0.1)};
        }
      `;
  }
  if (!isBottom && isActive) {
    return css`
        background-color: ${darken(theme.palette.black[80], theme.palette.tonalOffset)};
      `;
  }
  if (isBottom && isActive) {
    return css`
        color: ${theme.palette.primary.main};

        :after {
          border-bottom: 2px solid ${theme.palette.text.primary};
          bottom: 0;
          content: '';
          left: 0;
          position: absolute;
          right: 0;
        }
      `;
  }
  if (isBottom && !isToggle) {
    return css`
        color: ${theme.palette.text.secondary};

        &:hover,
        &:active {
          color: ${isDisabled ? theme.palette.text.secondary : theme.palette.primary.main};
        }
      `;
  }
  return css`
      &:hover,
      &:active {
        background-color: ${isDisabled ? 'transparent' : darken(theme.palette.black[80], theme.palette.tonalOffset)};
      }
    `;
}};
`;

const _excluded$1 = ["theme"];
const HeaderNavItemA = styled(A).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return _objectSpread2(_objectSpread2({
    position: 'relative',
    display: 'flex'
  }, theme.typography.button), {}, {
    m: 0,
    color: 'inherit',
    bg: 'inherit'
  }, props);
})`
  text-decoration: none;
  min-height: inherit;
  flex-direction: row;
  align-items: center;
  user-select: none;
  line-height: normal;
`;

const _excluded$2 = ["as", "to", "bottom", "active", "isToggle", "isToggleActive", "isDisabled", "icon", "iconProps", "iconOnly", "onClick", "children", "showBadge", "badgeText", "props", "styles"];
function HeaderNavItem(_ref) {
  let {
      as,
      to,
      bottom,
      active,
      isToggle,
      isToggleActive,
      isDisabled = false,
      icon = null,
      iconProps = null,
      iconOnly = false,
      onClick,
      children,
      showBadge,
      badgeText,
      props = {},
      styles = {}
    } = _ref,
    restProps = _objectWithoutProperties(_ref, _excluded$2);
  const theme = useTheme();
  const getOverrideStyleProp = key => {
    return getStyleProp(key, styles, theme);
  };
  const handleOnClick = event => {
    if (isDisabled) {
      return;
    }
    if (onClick) {
      onClick(event);
    }
  };
  const menuLinkProps = _objectSpread2(_objectSpread2(_objectSpread2({}, props.a), restProps), {}, {
    as,
    to,
    onClick: event => handleOnClick(event)
  });
  const isIconOnly = iconOnly || !children;
  const renderItem = () => /*#__PURE__*/createElement(HeaderNavItemDiv, _extends({}, props.root, {
    isBottom: bottom,
    isActive: active,
    isToggle: isToggle,
    isToggleActive: isToggleActive,
    isDisabled: isDisabled
  }, getOverrideStyleProp('root')), /*#__PURE__*/createElement(HeaderNavItemA, _extends({}, menuLinkProps, {
    p: bottom ? `0 ${isIconOnly ? theme.spacing.sm + 1 : theme.spacing.sm}px` : [`0 ${isIconOnly ? theme.spacing.sm + 1 : theme.spacing.sm}px`, `0 ${isIconOnly ? theme.spacing.md + 2 : theme.spacing.md}px`],
    height: bottom ? `${theme.shape.headerBottomHeight}px` : [theme.shape.headerBottomHeight, theme.shape.headerTopHeight]
  }, getOverrideStyleProp('a')), icon || (iconProps ? /*#__PURE__*/createElement(Icon, iconProps) : null), !isIconOnly && children));
  return showBadge ? /*#__PURE__*/createElement(Badge, {
    iconOnly: true,
    isRound: true,
    text: badgeText,
    animationDisabled: true,
    styles: {
      root: {
        pointerEvents: 'none',
        top: `${theme.spacing.xs}px`
      }
    }
  }, renderItem()) : renderItem();
}

export default HeaderNavItem;
